// Smart routing manager — a priority-ordered list of rules that decide which agency
// user a new ticket is assigned to. Lowest priority (top of the list) wins; the first
// matching rule sets `assigned_to`, else we fall back to per-sub-account assignments,
// then the generic pot. Rule types: feature_group (a taxonomy group), saas_plan (a
// client's plan), subaccount (a specific location). Saved whole via PUT /routing-rules.
import { useEffect, useState } from 'react';
import type {
  AgencyUser,
  ClientAssignment,
  FeatureGroup,
  Plan,
  RoutingRule,
  RoutingRuleType,
} from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { Button, Card, Chip, EmptyNote, ErrorNote, Select, Spinner, Toggle } from './ui';

const UNASSIGNED = '__pot__';

const RULE_TYPES: { id: RoutingRuleType; label: string }[] = [
  { id: 'feature_group', label: 'Feature group' },
  { id: 'saas_plan', label: 'SaaS plan' },
  { id: 'subaccount', label: 'Sub-account' },
];
// A client-side id for freshly-added rows (the Worker assigns the real id on save).
function tempId(): string {
  return `new-${Math.random().toString(36).slice(2, 9)}`;
}

export default function Routing({ users }: { users: AgencyUser[] }) {
  const [rules, setRules] = useState<RoutingRule[] | null>(null);
  const [groups, setGroups] = useState<FeatureGroup[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subaccounts, setSubaccounts] = useState<ClientAssignment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Promise.all([
      agencyApi.routingRules(),
      agencyApi.taxonomy(),
      agencyApi.plansConfig(),
      agencyApi.assignments(),
    ])
      .then(([r, g, p, a]) => {
        setRules([...r].sort((x, y) => x.priority - y.priority));
        setGroups(g);
        setPlans(p);
        setSubaccounts(a);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  function mutate(next: RoutingRule[]) {
    // Keep priority in lockstep with list order (0 = top = wins first).
    setRules(next.map((r, i) => ({ ...r, priority: i })));
    setDirty(true);
    setSaved(false);
  }

  function addRule() {
    if (!rules) return;
    const rule: RoutingRule = {
      id: tempId(),
      priority: rules.length,
      rule_type: 'feature_group',
      match_value: groups[0]?.key ?? '',
      agency_user_id: users[0]?.id ?? null,
      label: null,
      active: true,
    };
    mutate([...rules, rule]);
  }

  function patchRule(id: string, patch: Partial<RoutingRule>) {
    if (!rules) return;
    mutate(rules.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function removeRule(id: string) {
    if (!rules) return;
    mutate(rules.filter((r) => r.id !== id));
  }

  function move(index: number, dir: -1 | 1) {
    if (!rules) return;
    const target = index + dir;
    if (target < 0 || target >= rules.length) return;
    const next = [...rules];
    [next[index], next[target]] = [next[target], next[index]];
    mutate(next);
  }

  // When the rule type changes, reset the match value to a sane default for that type.
  function changeType(id: string, rule_type: RoutingRuleType) {
    let match_value = '';
    if (rule_type === 'feature_group') match_value = groups[0]?.key ?? '';
    else if (rule_type === 'saas_plan') match_value = plans[0]?.id ?? '';
    else if (rule_type === 'subaccount') match_value = subaccounts[0]?.location_id ?? '';
    patchRule(id, { rule_type, match_value });
  }

  async function save() {
    if (!rules) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await agencyApi.saveRoutingRules(rules);
      setRules([...updated].sort((x, y) => x.priority - y.priority));
      setDirty(false);
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (error && !rules) return <ErrorNote error={error} />;
  if (!rules) return <Spinner label="Loading routing rules…" />;

  return (
    <div className="space-y-4">
      <Card className="border-brand/20 bg-brand/5 p-4 text-sm text-slate-600">
        <span className="font-medium text-slate-800">How routing works.</span> New tickets are
        checked against these rules top-to-bottom — <span className="font-medium">lowest priority
        wins</span>. The first rule that matches sets the assignee. For example, an{' '}
        <span className="font-medium">automations specialist</span> rule (feature group ={' '}
        <span className="font-mono text-xs">Automation</span>) near the top means they get{' '}
        <em>every</em> workflow ticket, regardless of which client raised it. No match falls back to
        the client's per-sub-account assignment, then the generic pot.
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="secondary" onClick={addRule}>
          + Add rule
        </Button>
        <div className="flex items-center gap-3">
          {saved && !dirty && <span className="text-xs text-emerald-600">Saved</span>}
          {error && <span className="text-xs text-rose-600">{error}</span>}
          <Button onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save rules'}
          </Button>
        </div>
      </div>

      {rules.length === 0 ? (
        <EmptyNote>
          No routing rules yet. Add one to send tickets for a feature group, plan, or sub-account to a
          specific team member.
        </EmptyNote>
      ) : (
        <Card className="divide-y divide-slate-100">
          {rules.map((rule, i) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              index={i}
              total={rules.length}
              users={users}
              groups={groups}
              plans={plans}
              subaccounts={subaccounts}
              onType={(t) => changeType(rule.id, t)}
              onPatch={(p) => patchRule(rule.id, p)}
              onMove={(dir) => move(i, dir)}
              onRemove={() => removeRule(rule.id)}
            />
          ))}
        </Card>
      )}
    </div>
  );
}

function RuleRow({
  rule,
  index,
  total,
  users,
  groups,
  plans,
  subaccounts,
  onType,
  onPatch,
  onMove,
  onRemove,
}: {
  rule: RoutingRule;
  index: number;
  total: number;
  users: AgencyUser[];
  groups: FeatureGroup[];
  plans: Plan[];
  subaccounts: ClientAssignment[];
  onType: (t: RoutingRuleType) => void;
  onPatch: (p: Partial<RoutingRule>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <div className={`flex flex-wrap items-center gap-3 p-4 ${rule.active ? '' : 'opacity-60'}`}>
      {/* Priority + reorder */}
      <div className="flex items-center gap-2">
        <div className="flex flex-col">
          <button
            onClick={() => onMove(-1)}
            disabled={index === 0}
            className="text-slate-400 hover:text-brand disabled:opacity-30"
            aria-label="Move up"
            title="Higher priority"
          >
            ▲
          </button>
          <button
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            className="text-slate-400 hover:text-brand disabled:opacity-30"
            aria-label="Move down"
            title="Lower priority"
          >
            ▼
          </button>
        </div>
        <Chip label={`#${index + 1}`} muted />
      </div>

      {/* Rule type */}
      <Select value={rule.rule_type} onChange={(v) => onType(v as RoutingRuleType)}>
        {RULE_TYPES.map((t) => (
          <option key={t.id} value={t.id}>{t.label}</option>
        ))}
      </Select>

      <span className="text-sm text-slate-400">is</span>

      {/* Match value — picker keyed to the rule type (no free-text IDs) */}
      <MatchValuePicker
        rule={rule}
        groups={groups}
        plans={plans}
        subaccounts={subaccounts}
        onChange={(match_value) => onPatch({ match_value })}
      />

      <span className="text-sm text-slate-400">→ assign to</span>

      {/* Assignee */}
      <Select
        value={rule.agency_user_id ?? UNASSIGNED}
        onChange={(v) => onPatch({ agency_user_id: v === UNASSIGNED ? null : v })}
      >
        <option value={UNASSIGNED}>Generic pot</option>
        {users.map((u) => (
          <option key={u.id} value={u.id}>{u.name}</option>
        ))}
      </Select>

      <div className="ml-auto flex items-center gap-3">
        <Toggle checked={rule.active} onChange={(v) => onPatch({ active: v })} label="Active" />
        <button
          onClick={onRemove}
          className="text-sm text-rose-500 hover:text-rose-700"
          title="Delete rule"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function MatchValuePicker({
  rule,
  groups,
  plans,
  subaccounts,
  onChange,
}: {
  rule: RoutingRule;
  groups: FeatureGroup[];
  plans: Plan[];
  subaccounts: ClientAssignment[];
  onChange: (v: string) => void;
}) {
  if (rule.rule_type === 'feature_group') {
    return (
      <Select value={rule.match_value} onChange={onChange}>
        {groups.length === 0 && <option value="">No feature groups</option>}
        {groups.map((g) => (
          <option key={g.key} value={g.key}>{g.label}</option>
        ))}
      </Select>
    );
  }
  if (rule.rule_type === 'saas_plan') {
    return (
      <Select value={rule.match_value} onChange={onChange}>
        {plans.length === 0 && <option value="">No plans</option>}
        {plans.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </Select>
    );
  }
  // subaccount
  return (
    <Select value={rule.match_value} onChange={onChange}>
      {subaccounts.length === 0 && <option value="">No sub-accounts</option>}
      {subaccounts.map((s) => (
        <option key={s.location_id} value={s.location_id}>
          {s.client_name ?? s.location_id}
        </option>
      ))}
    </Select>
  );
}
